import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import CrtDisplay from './CrtDisplay';
import '../App.css';

function DesignerLeaderboard() {
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDesignerStats();
  }, []);

  const loadDesignerStats = async () => {
    try {
      const { data: submissions, error } = await supabase
        .from('submissions')
        .select(`
          id,
          designer_name,
          game_id,
          created_at,
          games(id, game_name, game_date, created_at)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading submissions:', error);
        setLoading(false);
        return;
      }

      const { data: scores, error: scoresError } = await supabase
        .from('scores')
        .select('submission_id, score');

      if (scoresError) {
        console.error('Error loading scores:', scoresError);
        setLoading(false);
        return;
      }

      const designerStats = {};

      submissions.forEach(submission => {
        const designerName = submission.designer_name;
        const gameId = submission.game_id;
        const game = Array.isArray(submission.games) ? submission.games[0] : submission.games;
        const gameDate = game?.game_date || game?.created_at;
        const submissionDate = submission.created_at;

        if (!designerStats[designerName]) {
          designerStats[designerName] = {
            name: designerName,
            gamesPlayed: new Set(),
            totalPoints: 0,
            submissionCount: 0,
            lastPlayedDate: null
          };
        }

        designerStats[designerName].gamesPlayed.add(gameId);
        designerStats[designerName].submissionCount += 1;

        const currentDate = gameDate || submissionDate;
        if (currentDate) {
          if (!designerStats[designerName].lastPlayedDate || 
              new Date(currentDate) > new Date(designerStats[designerName].lastPlayedDate)) {
            designerStats[designerName].lastPlayedDate = currentDate;
          }
        }

        const submissionScores = scores.filter(s => s.submission_id === submission.id);
        const submissionTotal = submissionScores.reduce((sum, s) => sum + (parseInt(s.score) || 0), 0);
        designerStats[designerName].totalPoints += submissionTotal;
      });

      const designersArray = Object.values(designerStats).map(designer => ({
        name: designer.name,
        gamesPlayed: designer.gamesPlayed.size,
        totalPoints: designer.totalPoints,
        submissionCount: designer.submissionCount,
        averageScore: designer.totalPoints / designer.submissionCount || 0,
        lastPlayedDate: designer.lastPlayedDate
      }));

      designersArray.sort((a, b) => b.totalPoints - a.totalPoints);

      setDesigners(designersArray);
      setLoading(false);
    } catch (error) {
      console.error('Error in loadDesignerStats:', error);
      setLoading(false);
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '---';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '---';
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');
      const yy = date.getFullYear().toString().slice(2);
      return `${mm}.${dd}.${yy}`;
    } catch {
      return '---';
    }
  };

  const pad = (str, len, align = 'left') => {
    const s = String(str).slice(0, len);
    if (align === 'right') return s.padStart(len, ' ');
    if (align === 'center') {
      const totalPad = len - s.length;
      const leftPad = Math.floor(totalPad / 2);
      return ' '.repeat(leftPad) + s + ' '.repeat(totalPad - leftPad);
    }
    return s.padEnd(len, ' ');
  };

  const formatRow = (rank, name, games, lastPlayed, total) => {
    return [
      pad(rank, 4),
      '  ',
      pad(name, 14),
      '  ',
      pad(games, 12, 'center'),
      '      ',
      pad(lastPlayed, 11),
      '      ',
      pad(total, 8, 'right')
    ].join('');
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1100px', width: '95%', margin: '0 auto', padding: '2rem 0' }}>
        <div style={{
          background: '#0a1628',
          border: '3px solid rgba(215, 2, 246, 0.5)',
          borderRadius: '14px',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <p style={{ 
            color: '#8ec8f8', 
            fontFamily: "'JetBrains Mono', monospace",
            textShadow: '0 0 6px rgba(100, 170, 255, 0.4)'
          }}>
            LOADING DESIGNER STATS...
          </p>
        </div>
      </div>
    );
  }

  if (designers.length === 0) {
    return (
      <div style={{ maxWidth: '1100px', width: '95%', margin: '0 auto', padding: '2rem 0' }}>
        <div style={{
          background: '#0a1628',
          border: '3px solid rgba(215, 2, 246, 0.5)',
          borderRadius: '14px',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <p style={{ 
            color: '#8ec8f8', 
            fontFamily: "'JetBrains Mono', monospace",
            textShadow: '0 0 6px rgba(100, 170, 255, 0.4)'
          }}>
            NO DESIGNERS HAVE PARTICIPATED YET
          </p>
        </div>
      </div>
    );
  }

  const headerText = formatRow('RANK', 'DESIGNER', 'GAMES PLAYED', 'LAST PLAYED', 'POINTS');
  const dividerText = '-'.repeat(headerText.length);

  const dataRows = designers.map((designer, index) => ({
    key: designer.name,
    text: formatRow(
      `${index + 1}`,
      designer.name.toUpperCase(),
      `${designer.gamesPlayed}`,
      formatDateShort(designer.lastPlayedDate),
      `${designer.totalPoints}`
    )
  }));

  const crtRows = [
    { key: 'header', text: headerText, isHeader: true },
    { key: 'divider', text: dividerText, isHeader: true },
    ...[...dataRows].reverse()
  ];

  return (
    <div style={{ maxWidth: '1100px', width: '95%', margin: '0 auto', padding: '2rem 0' }}>
      <CrtDisplay
        rows={crtRows}
        typingSpeed={14}
        rowDelay={60}
      />
    </div>
  );
}

export default DesignerLeaderboard;
